/**
 * Builds the v2-pipeline plan for `assign`. `RESEQUENCE_DAILY` runs only
 * when `allOrderedStops` is provided — the assign endpoint always lands new
 * stops at sequence 1, so positioning requires a full-route resequence
 * after.
 */
import { type CseId, type WorkOrderId } from 'types/entities/shared/ids'
import { isDailyStop, isOnRequestStop } from 'types/modules/routing'

import { dayOfWeekForServiceDate } from '../dayOfWeek'
import { makeAssignDailyStep } from '../steps/assignDaily'
import { makeAssignMasterStep } from '../steps/assignMaster'
import { makeCascadeFutureDailyStep } from '../steps/cascadeFutureDaily'
import { makePreflightStep } from '../steps/preflight'
import { makeSequenceDailyStep } from '../steps/sequenceDaily'
import { makeVerifyStep } from '../steps/verify'

import type { AssignIntent, Deps, OperationPlan, PipelineStep } from 'types/modules/routing'

const STRATEGY_ID = 'v2-pipeline' as const

const collectIds = (
  intent: AssignIntent
): {
  readonly workOrderIds: ReadonlyArray<WorkOrderId>
  readonly cseIds: ReadonlyArray<CseId>
} => {
  const workOrderIds: WorkOrderId[] = []
  const cseIds: CseId[] = []
  const seenWo = new Set<string>()
  const seenCse = new Set<string>()
  for (const stop of intent.stops) {
    if (isDailyStop(stop) || isOnRequestStop(stop)) {
      const wo = stop.workOrderId
      if (wo && !seenWo.has(wo)) {
        seenWo.add(wo)
        workOrderIds.push(wo)
      }
      if (isDailyStop(stop) && stop.configuredServiceEventId) {
        const cse = stop.configuredServiceEventId
        if (!seenCse.has(cse)) {
          seenCse.add(cse)
          cseIds.push(cse)
        }
      }
    }
  }
  return { workOrderIds, cseIds }
}

export const buildAssignPipeline = (intent: AssignIntent, deps: Deps): OperationPlan => {
  const steps: PipelineStep[] = []
  const { workOrderIds, cseIds } = collectIds(intent)
  const includeMaster = intent.includeMaster === true

  steps.push(makePreflightStep({ intent }, deps) as PipelineStep)

  if (workOrderIds.length > 0) {
    steps.push(
      makeAssignDailyStep(
        {
          routeId: intent.routeId,
          serviceDate: intent.serviceDate,
          workOrderIds,
          startSequence: intent.startSequence,
        },
        deps
      ) as PipelineStep
    )
  }

  if (intent.allOrderedStops && intent.allOrderedStops.length > 0) {
    steps.push(
      makeSequenceDailyStep(
        {
          routeId: intent.routeId,
          serviceDate: intent.serviceDate,
          orderedStops: intent.allOrderedStops,
        },
        deps
      ) as PipelineStep
    )
  }

  if (includeMaster && cseIds.length > 0) {
    const dayOfWeek = dayOfWeekForServiceDate(intent.serviceDate)
    if (dayOfWeek) {
      steps.push(
        makeAssignMasterStep(
          {
            routeId: intent.routeId,
            dayOfWeek,
            cseIds,
            futureStops: true,
            futureStart: intent.serviceDate,
            startSequence: intent.startSequence,
          },
          deps
        ) as PipelineStep
      )
    }
  }

  if (includeMaster && workOrderIds.length > 0) {
    steps.push(
      makeCascadeFutureDailyStep(
        {
          mode: 'assign',
          phase: 'ASSIGN_FUTURE_DAILY',
          routeId: intent.routeId,
          currentServiceDate: intent.serviceDate,
          workOrderIds,
        },
        deps
      ) as PipelineStep
    )
  }

  steps.push(
    makeVerifyStep(
      {
        intent,
        expectedAssignedWorkOrderIds: workOrderIds.map(String),
      },
      deps
    ) as PipelineStep
  )

  return {
    strategy: STRATEGY_ID,
    intent,
    steps,
  }
}
